import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Link} from 'gatsby';
import {Grid} from 'react-styled-flexboxgrid';
import styled from 'styled-components';
import LogoImgDark from '../../../assets/logos/MiurosLogoDark.svg';
import LogoImgLight from '../../../assets/logos/MiurosLogo.svg';
import {getColors, maxMediaQueries} from '../../utils/styling';
import Row from 'react-styled-flexboxgrid/src/components/Row';
import Hidden from '../../atoms/Hidden';
import Visible from '../../atoms/Visible';
import {NavLinks} from './NavLinks';
import {NavHamburger} from './NavHamburger';
import {Button, ExternalLinkButton} from '../../atoms/Button';
import {GlobalContext} from '../../context/GlobalContext';
import {LoginLink} from '../../atoms/LoginLink';

const NAVBAR_LINKS = [
  {
    title: 'Products',
    link: 'https://www.dixa.com/miuros-by-dixa'
  },
  {
    title: 'Pricing',
    link: 'https://www.dixa.com/pricing'
  },
  {
    title: 'Case Studies',
    link: 'https://www.dixa.com/customers'
  },
  {
    title: 'Blog',
    link: 'https://www.dixa.com/blog'
  },
  {
    title: 'Who We Are',
    link: 'https://www.dixa.com/about/'
  }
];

const NAVBAR_HEIGHTS = {
  lg: '96px',
  md: '64px'
};

const NavBarContainer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  height: ${NAVBAR_HEIGHTS.lg};
  background-color: ${({theme, isScrolled, variant}) => !isScrolled && (variant === 'dark') ?
          getColors(theme).gray800 :
          !isScrolled && (variant === 'light') ?
                  getColors(theme).gray100 :
                  getColors(theme).white};

  box-shadow: ${({isScrolled}) => !isScrolled ?
          'none' :
          '0 2px 8px rgba(74, 85, 104, 0.1)'};

  transition: background-color 300ms;

  ${maxMediaQueries.md`
    height: ${NAVBAR_HEIGHTS.md};
  `};

  .nav-link {
    font-size: 16px;
    color: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray300 : theme.colors.gray600};

    &:hover {
      color: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray100 : theme.colors.gray900};
    }
  }

  .navbar-container-grid {
    height: 100%;

    .navbar-container-row {
      margin: 0;
      height: 100%;
    }

    .navbar-container-logo {
      position: relative;
      z-index: 2;
      width: 92px;
      height: 32px;
    }

    .center-links {
      display: flex;
      flex-direction: row;
      flex: 1;
      padding-left: 64px;
      align-items: center;

      a, button {
        margin-right: 40px;
      }
    }

    .app-actions {
      display: flex;
      flex-direction: row;
      align-items: center;

      a {
        margin-right: 16px;
      }
    }

    .mobile-actions-container {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      width: 100%;
    }
  }

`;

const NavSpacer = styled.div`
  height: ${props => (props.hasBanner ? '114px' : NAVBAR_HEIGHTS.lg)};

  ${maxMediaQueries.md`
    height: ${props => (props.hasBanner ? '132px' : NAVBAR_HEIGHTS.md)};
  `};
`;

const NavBar = (props) => {

  const isDark = function(pathname) {
    switch (pathname) {
      case '/':
        return true;
      case '/404/':
        return true;
      case '/customer-service-qa/':
        return true;
      case '/who-we-are/':
        return true;
      case '/customer-service-analytics/':
        return true;
      case '/customer-service-automation/':
        return true;
      case '/pricing/':
        return true;
      case '/integrations/':
        return true;
      default:
        return false;
    }
  };

  const variant = isDark(props.location.pathname) ? 'dark' : 'light';

  const [isScrolled, setIsScrolled] = useState(false);
  const [isDashboardUser, setIsDashboardUser] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 0);
  }, []);

  useEffect(() => {
    const dashboardUserRecordExists = !!localStorage.getItem('miuros.dashboard-is-user');
    setIsDashboardUser(dashboardUserRecordExists);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const context = useContext(GlobalContext);

  /*
   TODO ! rather than hiding/unhiding stuff better to render/unrender.
     There is no reason to load both of these components in the DOM.
     Also this causes an issue if the screen changes width and the Hamburger is open already, because the scroll lock remains in place
   */
  return (
      <>
        <NavBarContainer isScrolled={isScrolled} variant={isScrolled ? 'light' : variant}>
          <Grid className="navbar-container-grid">
            <Row className="navbar-container-row" start="md" between="xs"
                 middle="xs">
              <a href="https://www.dixa.com/miuros-by-dixa">
                {
                  variant === 'dark' && !isScrolled ?
                      <img className="navbar-container-logo" src={LogoImgLight} alt="Miuros"/> :
                      <img className="navbar-container-logo" src={LogoImgDark} alt="Miuros"/>
                }
              </a>

              {/* DESKTOP */}

              <Visible lg>
                <NavLinks navbarLinks={NAVBAR_LINKS} variant={isScrolled ? 'light' : variant}/>
              </Visible>
              <Visible lg>
                <div className="app-actions">
                  {
                    !isDashboardUser ?
                        (<LoginLink href="/dashboard" variant={isScrolled ? 'light' : variant}>Login</LoginLink>) :
                        (<ExternalLinkButton accent href="/dashboard">Login</ExternalLinkButton>)
                  }
                  {
                    !isDashboardUser &&
                    <ExternalLinkButton primary href="https://www.dixa.com/demo">Book a Demo</ExternalLinkButton>
                  }
                </div>
              </Visible>

              {/* MOBILE */}

              <Hidden lg>
                <NavHamburger navbarLinks={NAVBAR_LINKS} variant={isScrolled ? 'light' : variant}/>
              </Hidden>

            </Row>
          </Grid>
        </NavBarContainer>

        <NavSpacer/>
      </>
  );

};

export default NavBar;
